import { useCallback } from "react";
import { Settings } from "../settings";
import { COIN_SYMBOL_IDS } from "../SymbolData";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";

/**
 * useReelLandingAnimations
 *
 * Optimized hook for triggering landing/idle animations on reels.
 * - Uses cached animation availability from OverlayReelController.
 * - Performs a single lookup per symbol instead of multiple hasAnimation calls.
 */
export const useReelLandingAnimations = () => {
  const { reelRefs, overlayReelRefs } = useReelRefs();
  const { suppressLandingAnimationsRef, suppressLandingAnimationsPerReelRef } =
    useReelControl();

  const triggerReelLandingAnimations = useCallback(
    (reelIndex, reelData) => {
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];
      const overlayController = overlayReelRef?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();

      if (!overlayController || !reelController || !reelData) {
        return;
      }

      const globalSuppress = suppressLandingAnimationsRef.current;
      const perReelSuppress =
        suppressLandingAnimationsPerReelRef.current.has(reelIndex);

      if (globalSuppress || perReelSuppress) {
        return;
      }

      // ReelControllerV2 passes pure 0-indexed API data (no buffers)
      // Loop through visible symbols: API indices 0-3 map to overlay indices 0-3
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolId = reelData[symbolIndex];

        if (symbolId === undefined) {
          continue;
        }

        const { hasLanding, hasIdle } =
          overlayController.getAvailableAnimations(symbolIndex, symbolId);

        if (hasLanding || hasIdle) {
          overlayController.showLandingAnimation(symbolIndex, symbolId);
          // Hide texture in next frame to prevent flash (same pattern as win animations)
          requestAnimationFrame(() => {
            if (reelController.hideSymbolTexture) {
              reelController.hideSymbolTexture(symbolIndex);
            }
          });
        } else {
          // Ensure texture is visible if no animation
          if (reelController.showSymbolTexture) {
            reelController.showSymbolTexture(symbolIndex);
          }
        }
      }
    },
    [
      overlayReelRefs,
      reelRefs,
      suppressLandingAnimationsRef,
      suppressLandingAnimationsPerReelRef,
    ]
  );

  return { triggerReelLandingAnimations };
};

/**
 * useInitialIdleAnimations
 *
 * Hook for triggering idle animations.
 * Two modes:
 * 1. Default (onlyTransitionWinAnimations = false): Shows idle animations for all symbols that have them
 * 2. Transition mode (onlyTransitionWinAnimations = true): Only transitions active win animations to idle
 */
export const useInitialIdleAnimations = () => {
  const { reelRefs, overlayReelRefs, dimReelSetRef } = useReelRefs();

  const triggerInitialIdleAnimations = useCallback(
    (onlyTransitionWinAnimations = false) => {
      for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
        const overlayReelRef = overlayReelRefs.current?.[reelIndex];
        const overlayController = overlayReelRef?.getOverlayReelController();
        const reelController =
          reelRefs.current?.[reelIndex]?.getReelController();

        if (!overlayController || !reelController) {
          continue;
        }

        if (onlyTransitionWinAnimations) {
          // Mode 1: Clear all active animations and play idle for all symbols that have it

          // First, hide all active animations to properly clear spine state
          overlayController.hideAllAnimations();

          // Then loop through all visible symbols and play idle if available
          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Get the symbol ID from ReelControllerV2
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            // Check if this symbol has an idle animation
            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId
            );

            if (hasIdle) {
              // Show idle animation
              overlayController.showIdleAnimation(symbolIndex, symbolId);
              // Texture is already hidden from previous animations, so no need to hide again
            }
          }
        } else {
          // Mode 2: Show idle for all symbols that have it (initial game load)
          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Get the symbol ID from ReelControllerV2
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            // Check if this symbol has an idle animation
            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId
            );

            if (hasIdle) {
              // Show idle animation
              overlayController.showIdleAnimation(symbolIndex, symbolId);

              // Hide texture in next frame to prevent flash
              requestAnimationFrame(() => {
                if (reelController.hideSymbolTexture) {
                  reelController.hideSymbolTexture(symbolIndex);
                }
              });
            }
          }
        }
      }

      // Remove dimming overlay after transitioning animations (only in transition mode)
      if (onlyTransitionWinAnimations && dimReelSetRef.current) {
        dimReelSetRef.current.undimReels();
      }
    },
    [overlayReelRefs, reelRefs, dimReelSetRef]
  );

  return { triggerInitialIdleAnimations };
};

/**
 * useHoldAndWinLandingAnimations
 *
 * Hook for triggering landing animations for individual Hold & Win symbols
 * Similar to useReelLandingAnimations but works on a per-symbol basis
 *
 * Note: Initial idle animations are handled per-symbol in HoldAndWinOverlaySymbol
 * to ensure proper timing and controller initialization
 */
export const useHoldAndWinLandingAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinSymbolLandingAnimation = useCallback(
    (symbolIndex, reelIndex, symbolId, onTextureHiddenCallback) => {
      // Get overlay symbol controller
      const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      const overlaySymbolController = overlaySymbolRef?.getController?.();

      // Get base symbol controller
      const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
      const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);
      const symbolController = symbolRef?.current?.getSymbolController?.();

      if (!overlaySymbolController || !symbolController) {
        return;
      }

      // Only show landing animation if it's a coin symbol
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        // Show spine landing animation (land -> idle sequence)
        overlaySymbolController.showLandingAnimation(symbolId);

        // Hide texture in next frame to prevent flash, then call conversion callback
        requestAnimationFrame(() => {
          if (symbolController && symbolController.hideSymbolTexture) {
            symbolController.hideSymbolTexture();
          }

          // Call conversion callback AFTER texture is hidden
          if (onTextureHiddenCallback) {
            onTextureHiddenCallback();
          }
        });
      }
    },
    [holdAndWinReelRefs, holdAndWinOverlayReelRefs]
  );

  return { triggerHoldAndWinSymbolLandingAnimation };
};

/**
 * useHoldAndWinIdleAnimations
 *
 * Hook for triggering idle animations for ALL Hold & Win symbols at once
 * Used during Hold and Win reload to show idle animations for existing coins
 */
export const useHoldAndWinIdleAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinIdleAnimations = useCallback(() => {
    // Loop through all 5 reels
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      // Loop through all 4 symbol positions
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        // Get overlay symbol controller
        const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const overlaySymbolController = overlaySymbolRef?.getController?.();

        // Get base symbol controller
        const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
        const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);

        if (!symbolRef || !symbolRef.current) {
          continue;
        }

        // symbolRef.current should have getSymbolController method
        const symbolController = symbolRef.current.getSymbolController?.();

        if (!overlaySymbolController || !symbolController) {
          continue;
        }

        // Check if base symbol has a coin
        const symbolId = symbolController.symbolId;
        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          // Show idle animation only (no landing animation)
          overlaySymbolController.showIdleAnimation(symbolId);

          // Hide texture in next frame to prevent flash
          requestAnimationFrame(() => {
            if (symbolController.hideSymbolTexture) {
              symbolController.hideSymbolTexture();
            } else {
              console.warn("⚠️ [triggerHoldAndWinIdleAnimations] hideSymbolTexture method missing!", {
                reelIndex,
                symbolIndex,
                symbolId
              });
            }
          });
        }
      }
    }
  }, [holdAndWinReelRefs, holdAndWinOverlayReelRefs]);

  return { triggerHoldAndWinIdleAnimations };
};
